import { useEffect, useState } from 'react';
import type { TextStyle, TextVariant } from '@vd/annotation-core';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Textarea, cn } from '@vd/ui';

// Canonical definitions live in @vd/annotation-core (the Shape union's
// textBox variant references them); re-exported so the modal's callers
// keep importing presentation types from the component that edits them.
export type { TextStyle, TextVariant };

export interface TextBoxModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { text: string; variant: TextVariant; style: TextStyle }) => void;
  initial?: { text?: string; variant?: TextVariant; style?: TextStyle };
  /** When true, the modal only edits the defaults — no text content required. */
  defaultsMode?: boolean;
  /**
   * When true, hide the text field and only edit variant + style of an
   * existing shape. The caller seeds `initial.text` with the shape's current
   * text; it passes through unchanged to onSave so the text content isn't
   * disturbed.
   */
  styleOnly?: boolean;
}

const STYLES: Array<{ key: TextStyle; label: string }> = [
  { key: 'red-outline', label: 'Red / white outline' },
  { key: 'red',         label: 'Red' },
  { key: 'black',       label: 'Black' },
  { key: 'white',       label: 'White (with shadow)' },
];

export function TextBoxModal({ open, onClose, onSave, initial, defaultsMode, styleOnly }: TextBoxModalProps) {
  const hideText = defaultsMode || styleOnly;
  const [variant, setVariant] = useState<TextVariant>(initial?.variant ?? 'plain');
  const [style, setStyle] = useState<TextStyle>(initial?.style ?? 'red-outline');
  const [text, setText] = useState(initial?.text ?? '');

  useEffect(() => {
    if (open) {
      setVariant(initial?.variant ?? 'plain');
      setStyle(initial?.style ?? 'red-outline');
      setText(initial?.text ?? '');
    }
  }, [open, initial?.variant, initial?.style, initial?.text]);

  return (
    <Modal open={open} onClose={onClose} width={520}>
      <ModalHeader>{defaultsMode ? 'Default text annotation style' : styleOnly ? 'Edit text style' : 'Add text annotation'}</ModalHeader>
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Variant</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <VariantCard
                active={variant === 'plain'}
                onClick={() => setVariant('plain')}
                title="Just text"
                desc="Text only, no background."
                preview={<div style={{ color: '#EF4444', fontWeight: 600 }}>Look here</div>}
              />
              <VariantCard
                active={variant === 'sticky'}
                onClick={() => setVariant('sticky')}
                title="Sticky note"
                desc="Yellow note background."
                preview={
                  <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', color: '#111', padding: '4px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>
                    Look here
                  </div>
                }
              />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Text style</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {STYLES.map((s) => (
                <label
                  key={s.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px',
                    border: `1px solid ${style === s.key ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 8,
                    background: style === s.key ? 'var(--bg-subtle)' : 'var(--surface)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    checked={style === s.key}
                    onChange={() => setStyle(s.key)}
                    style={{ margin: 0 }}
                  />
                  <StylePreview style={s.key} />
                  <span style={{ fontSize: 12 }}>{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          {!hideText && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Text</div>
              <Textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your note…"
                style={{ minHeight: 80 }}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && text.trim()) {
                    onSave({ text, variant, style });
                    onClose();
                  }
                }}
              />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                ⌘↵ to save · the style you pick here also becomes the default for the next text box.
              </div>
            </div>
          )}
          {defaultsMode && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: 6 }}>
              These settings become the default for every new text annotation on this ticket. You can still change any
              individual text box by clicking it and pressing <strong>Style</strong>.
            </div>
          )}
          {styleOnly && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: 6 }}>
              Updates the variant and color of this text box. The text content stays the same. Edit it directly on the canvas.
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          disabled={!hideText && !text.trim()}
          onClick={() => { onSave({ text, variant, style }); onClose(); }}
        >
          {defaultsMode ? 'Save as default' : styleOnly ? 'Update style' : 'Add to screenshot'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function VariantCard({ active, onClick, title, desc, preview }: { active: boolean; onClick: () => void; title: string; desc: string; preview: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn()}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
        padding: 12,
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        background: active ? 'var(--bg-subtle)' : 'var(--surface)',
        borderRadius: 8,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 50, border: `2px solid ${active ? 'var(--accent)' : 'var(--border-strong)'}`, background: active ? 'var(--accent)' : 'transparent', position: 'relative' }}>
          {active && <span style={{ position: 'absolute', inset: 3, borderRadius: 50, background: 'var(--accent-text)' }} />}
        </span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{title}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{desc}</div>
      <div style={{ padding: '10px 0 0' }}>{preview}</div>
    </button>
  );
}

function StylePreview({ style }: { style: TextStyle }) {
  const base: React.CSSProperties = {
    width: 60,
    padding: '2px 6px',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'center',
    borderRadius: 4,
    background: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04) 4px, transparent 4px, transparent 8px)',
  };
  if (style === 'red-outline') {
    return <span style={{ ...base, color: '#EF4444', textShadow: '0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff' }}>Text</span>;
  }
  if (style === 'red')   return <span style={{ ...base, color: '#EF4444' }}>Text</span>;
  if (style === 'black') return <span style={{ ...base, color: '#111' }}>Text</span>;
  return <span style={{ ...base, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>Text</span>;
}
